/**
 * A one-time code typed into a row of single-digit boxes.
 *
 * Generic on purpose: it knows how a numeric code is typed, pasted, autofilled,
 * navigated and marked wrong, and nothing about where the code came from or
 * what it unlocks. Whoever mounts it listens for `otp:complete` and
 * `otp:submit` and decides what those mean on their screen.
 *
 * The state rules are pure functions over an array of digits, so they are
 * tested without a DOM (tests/js/otp-code.test.mjs); mountOtpCode() only
 * renders that array into the boxes and routes events into it.
 */

/** Only the digits of whatever was typed, pasted or autofilled. */
export function sanitizeDigits(value) {
  return String(value ?? '').replace(/\D+/g, '')
}

/**
 * What a box holds after an input event, and where focus goes next.
 *
 * One digit fills this box and moves on. Several digits are a paste or an
 * autofill: a whole code replaces everything from the first box - an OS
 * autofill lands in whichever box has focus - and anything shorter fills
 * forward from this box. A box that already held a digit and received a
 * second one keeps the new one, since the old one is what the caret was after.
 *
 * @param {string[]} digits current digits, '' for an empty box
 * @param {number} index the box the input happened in
 * @param {string} typed that box's raw value after the input
 * @returns {{digits: string[], focusIndex: number}}
 */
export function applyInput(digits, index, typed) {
  const next = [...digits]
  const length = next.length
  let clean = sanitizeDigits(typed)

  if (clean === '') {
    next[index] = ''
    return { digits: next, focusIndex: index }
  }

  if (clean.length === 2 && clean[0] === digits[index]) {
    clean = clean[1]
  }

  const start = clean.length >= length ? 0 : index
  const chars = clean.slice(0, length - start).split('')

  chars.forEach((char, offset) => {
    next[start + offset] = char
  })

  return { digits: next, focusIndex: Math.min(start + chars.length, length - 1) }
}

/**
 * Backspace clears this box; on an empty box it clears the one before and
 * steps back to it, which is what makes deleting a whole code one key held down.
 */
export function applyBackspace(digits, index) {
  const next = [...digits]

  if (next[index]) {
    next[index] = ''
    return { digits: next, focusIndex: index }
  }

  if (index === 0) {
    return { digits: next, focusIndex: 0 }
  }

  next[index - 1] = ''
  return { digits: next, focusIndex: index - 1 }
}

export function isComplete(digits) {
  return digits.length > 0 && digits.every((digit) => /^\d$/.test(digit))
}

/**
 * Bind a rendered <x-otp-code> to its behaviour.
 *
 * @param {HTMLElement|null} root the element carrying [data-otp]
 * @returns {{readonly value: string, readonly complete: boolean, clear(): void, focus(): void, setInvalid(invalid: boolean): void}|null}
 */
export function mountOtpCode(root) {
  if (!root) return null

  const boxes = Array.from(root.querySelectorAll('[data-otp-digit]'))
  const hidden = root.querySelector('[data-otp-value]')

  if (boxes.length === 0) return null

  let digits = boxes.map(() => '')

  const render = () => {
    boxes.forEach((box, i) => {
      box.value = digits[i]
      box.classList.toggle('is-filled', digits[i] !== '')
    })

    if (hidden) hidden.value = digits.join('')
  }

  const setInvalid = (invalid) => {
    root.classList.toggle('is-invalid', invalid)
    boxes.forEach((box) => {
      if (invalid) box.setAttribute('aria-invalid', 'true')
      else box.removeAttribute('aria-invalid')
    })
  }

  const emit = (name) => {
    root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { value: digits.join('') } }))
  }

  const focusBox = (index) => {
    const box = boxes[Math.max(0, Math.min(index, boxes.length - 1))]
    box.focus()
    box.select()
  }

  const commit = (result) => {
    const wasComplete = isComplete(digits)

    digits = result.digits
    render()
    setInvalid(false)
    focusBox(result.focusIndex)

    if (!wasComplete && isComplete(digits)) emit('otp:complete')
  }

  boxes.forEach((box, index) => {
    box.addEventListener('input', () => {
      commit(applyInput(digits, index, box.value))
    })

    // Handled here rather than left to the input event, which would only ever
    // see what fits the one box the paste landed in.
    box.addEventListener('paste', (event) => {
      const text = event.clipboardData?.getData('text') ?? ''

      event.preventDefault()
      if (sanitizeDigits(text) === '') return

      commit(applyInput(digits, index, text))
    })

    box.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'Backspace':
          event.preventDefault()
          commit(applyBackspace(digits, index))
          break
        case 'Delete':
          event.preventDefault()
          commit({ digits: digits.map((d, i) => (i === index ? '' : d)), focusIndex: index })
          break
        case 'ArrowLeft':
          event.preventDefault()
          focusBox(index - 1)
          break
        case 'ArrowRight':
          event.preventDefault()
          focusBox(index + 1)
          break
        case 'Home':
          event.preventDefault()
          focusBox(0)
          break
        case 'End':
          event.preventDefault()
          focusBox(boxes.length - 1)
          break
        case 'Enter':
          event.preventDefault()
          emit('otp:submit')
          break
      }
    })

    box.addEventListener('focus', () => box.select())
  })

  render()

  return {
    get value() {
      return digits.join('')
    },
    get complete() {
      return isComplete(digits)
    },
    clear() {
      digits = boxes.map(() => '')
      render()
      setInvalid(false)
    },
    focus() {
      const firstEmpty = digits.findIndex((digit) => digit === '')
      focusBox(firstEmpty === -1 ? boxes.length - 1 : firstEmpty)
    },
    setInvalid,
  }
}
